import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PRO } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import ProOnboardingChecklist from "@/components/ProOnboardingChecklist";
import {
  Check, Code2, BadgeCheck, Map as MapIcon, Megaphone, Gift, BarChart3, Globe,
  Wand2, Sparkles, ChevronRight, ChevronDown, ShieldCheck, Camera, Wine, ScrollText,
  MessageCircle, X, Send, Loader2, LayoutGrid, Building2, CalendarClock,
} from "lucide-react";
import { toast, Toaster } from "sonner";

// ─────────── Pro feature catalog ───────────
const FEATURES = [
  {
    key: "claim",
    icon: BadgeCheck,
    title: { fr: "Fiche producteur revendiquée", en: "Verified producer profile" },
    pitch: {
      fr: "Devenez propriétaire de votre fiche officielle sur geoVINUM. Modifiez vos infos, ajoutez vos visites, photos et événements.",
      en: "Take ownership of your official geoVINUM profile. Edit your info, add visits, photos and events.",
    },
    details: {
      fr: [
        "Badge 'Fiche vérifiée' visible par les visiteurs",
        "Synchronisation automatique avec Google Places (rating, horaires)",
        "Stats : nb de visiteurs, dégustations enregistrées, partages",
        "Modération par notre équipe sous 48 h",
      ],
      en: [
        "'Verified profile' badge visible to visitors",
        "Auto sync with Google Places (rating, hours)",
        "Stats: visitor count, recorded tastings, shares",
        "Reviewed by our team within 48h",
      ],
    },
    cta: { fr: "Voir un exemple", en: "See an example" },
    ctaTo: "/producers",
    tier: "amateur",
  },
  {
    key: "map_builder",
    icon: MapIcon,
    title: { fr: "Map Builder · Parcours touristique IA", en: "AI Tourism Route Builder" },
    pitch: {
      fr: "geoVINUM AI construit en quelques secondes un parcours sur-mesure pour vos visiteurs (familles, œnophiles, groupes).",
      en: "geoVINUM AI builds tailored visitor routes in seconds (families, oenophiles, groups).",
    },
    details: {
      fr: [
        "3 audiences pré-paramétrées + audience personnalisée",
        "POI détaillés : chai, vigne, paysage, restaurant, hôtel",
        "Conseils pratiques de geoVINUM AI pour chaque étape",
        "Publié sur votre fiche publique, partageable",
      ],
      en: [
        "3 pre-set audiences + custom audience",
        "Detailed POIs: cellar, vineyard, view, restaurant, hotel",
        "geoVINUM AI's practical tips for each stop",
        "Published on your public profile, shareable",
      ],
    },
    cta: { fr: "Découvrir Map Builder", en: "Discover Map Builder" },
    ctaTo: "/pro/map-builder",
    tier: "pro",
  },
  {
    key: "experience",
    icon: CalendarClock,
    badge: { fr: "NEW", en: "NEW" },
    title: {
      fr: "geoVINUM Experience · Événements de dégustation clés en main",
      en: "geoVINUM Experience · Turn-key tasting events",
    },
    pitch: {
      fr: "Créez votre événement, vendez vos billets en ligne (Stripe), équipez vos serveurs d'un scanner QR et transformez la dégustation en jeu gamifié — le tout en 15 minutes.",
      en: "Create your event, sell tickets online (Stripe), equip your staff with a QR scanner, and turn the tasting into a gamified experience — all in 15 minutes.",
    },
    details: {
      fr: [
        "Billetterie Stripe Connect (breakdown transparent : brut → frais → commission → net)",
        "Codes participants opaques imprimables + billets PDF avec QR",
        "Scanner QR staff temps-réel + décrément atomique des stocks",
        "Gamification : équipes, quiz Academy, classement live, badges",
        "Emails de confirmation automatiques aux acheteurs",
        "KPIs événement : billets, doses servies, top vins, revenu net",
      ],
      en: [
        "Stripe Connect ticketing (transparent breakdown: gross → fees → commission → net)",
        "Printable opaque participant codes + PDF tickets with QR",
        "Real-time staff QR scanner + atomic stock decrements",
        "Gamification: teams, Academy-powered quizzes, live leaderboard, badges",
        "Automatic confirmation emails to buyers",
        "Event KPIs: tickets, doses served, top wines, net revenue",
      ],
    },
    cta: { fr: "Créer mon premier événement", en: "Create my first event" },
    ctaTo: "/pro/experience",
    tier: "pro",
  },
  {
    key: "story",
    icon: ScrollText,
    title: { fr: "Story Builder · Récit IA de votre domaine", en: "Story Builder · AI domain narrative" },
    pitch: {
      fr: "Histoire, terroir, philosophie, anecdotes — geoVINUM AI rédige le récit de votre domaine en 6 sections éditables.",
      en: "History, terroir, philosophy, anecdotes — geoVINUM AI writes your domain's narrative in 6 editable sections.",
    },
    details: {
      fr: [
        "6 sections : Hero / Histoire / Terroir / Vins / Philosophie / Visite",
        "Édition libre paragraph-par-paragraph",
        "Affiché sur votre fiche publique (storytelling vendeur)",
        "Re-génération ciblée si besoin",
      ],
      en: [
        "6 sections: Hero / History / Terroir / Wines / Philosophy / Visit",
        "Free paragraph-by-paragraph editing",
        "Displayed on your public profile (sales storytelling)",
        "Targeted regeneration when needed",
      ],
    },
    cta: { fr: "Voir une fiche storytellée", en: "See a storytold profile" },
    ctaTo: "/producers",
    tier: "pro",
  },
  {
    key: "sponsored",
    icon: Megaphone,
    title: { fr: "Quiz sponsorisés · Lead gen qualifié", en: "Sponsored quizzes · Qualified lead gen" },
    pitch: {
      fr: "Créez un quiz à votre marque. Les joueurs qui terminent acceptent de recevoir vos communications — vous récupérez des leads chauds.",
      en: "Build a branded quiz. Finishers opt-in to your communications — you get warm leads.",
    },
    details: {
      fr: [
        "Quiz généré par IA selon votre thématique (appellation, cépage…)",
        "Récompense fournie (visite offerte, remise, etc.)",
        "Email automatique avec coordonnées du lead + score",
        "Suivi des leads dans votre Pro Dashboard",
      ],
      en: [
        "Quiz built by AI on your theme (appellation, grape…)",
        "Reward supplied (free visit, discount, etc.)",
        "Auto-email with lead contact + score",
        "Lead tracking in your Pro Dashboard",
      ],
    },
    cta: { fr: "Voir le module sponsorisé", en: "See the sponsored module" },
    ctaTo: "/pro/sponsored",
    tier: "pro",
  },
  {
    key: "dashboard",
    icon: BarChart3,
    title: { fr: "Pro Dashboard · Stats & leads", en: "Pro Dashboard · Stats & leads" },
    pitch: {
      fr: "Suivez en temps réel vos visiteurs, leads, dégustations enregistrées et partages de votre fiche.",
      en: "Real-time tracking of your visitors, leads, recorded tastings and profile shares.",
    },
    details: {
      fr: [
        "Vues fiches / vues itinéraire / contacts cliqués",
        "Leads quiz sponsorisé avec coordonnées",
        "Export CSV des leads",
        "Alertes email hebdo",
      ],
      en: [
        "Profile views / route views / contacts clicked",
        "Sponsored-quiz leads with contact info",
        "CSV export of leads",
        "Weekly email alerts",
      ],
    },
    cta: { fr: "Aperçu du dashboard", en: "Dashboard preview" },
    ctaTo: "/pro/dashboard",
    tier: "pro",
  },
  {
    key: "iframe",
    icon: Globe,
    title: { fr: "Widgets iframe · Intégrez sur votre site", en: "iFrame widgets · Embed on your site" },
    pitch: {
      fr: "Embarquez votre carte interactive, vos cuvées et votre quiz sur votre propre site en 2 minutes (1 ligne de code).",
      en: "Embed your interactive map, cuvées and quiz on your own site in 2 minutes (1 line of code).",
    },
    details: {
      fr: [
        "Widget Carte (parcellaire + appellation)",
        "Widget Cuvées (catalogue + accords)",
        "Widget Quiz interactif",
        "Branding personnalisé (couleurs, logo)",
      ],
      en: [
        "Map widget (plots + appellation)",
        "Cuvées widget (catalog + pairings)",
        "Interactive quiz widget",
        "Custom branding (colours, logo)",
      ],
    },
    cta: { fr: "Voir les widgets disponibles", en: "See available widgets" },
    ctaTo: "/cepages",
    tier: "pro",
  },
  {
    key: "discovery",
    icon: Camera,
    title: { fr: "Boost de visibilité IA", en: "AI visibility boost" },
    pitch: {
      fr: "Apparaissez dans les suggestions geoVINUM AI, les recommandations 'cuvées similaires' et les itinéraires générés pour les voyageurs.",
      en: "Show up in geoVINUM AI suggestions, 'similar wines' recommendations and traveler-generated itineraries.",
    },
    details: {
      fr: [
        "Indexation prioritaire dans les recos IA",
        "Apparition dans les itinéraires générés par les voyageurs",
        "Badge ⭐ 'IA-recommended' sur certaines requêtes",
        "Score de pertinence boosté avec contenu enrichi",
      ],
      en: [
        "Priority indexing in AI recommendations",
        "Appearance in traveler-generated routes",
        "⭐ 'AI-recommended' badge on certain queries",
        "Boosted relevance score with enriched content",
      ],
    },
    cta: { fr: "Voir geoVINUM AI en action", en: "See geoVINUM AI in action" },
    ctaTo: "/",
    tier: "pro",
  },
  {
    key: "api",
    icon: Code2,
    title: { fr: "API & data export", en: "API & data export" },
    pitch: {
      fr: "Accès programmatique à votre fiche, leads, stats et carnet de visiteurs via une API REST simple.",
      en: "Programmatic access to your profile, leads, stats and visitor notebook via a simple REST API.",
    },
    details: {
      fr: [
        "Token API par domaine",
        "Endpoints REST documentés",
        "Webhooks de leads en temps réel",
        "Export complet de vos données (RGPD-friendly)",
      ],
      en: [
        "Per-domain API token",
        "Documented REST endpoints",
        "Real-time lead webhooks",
        "Full data export (GDPR-friendly)",
      ],
    },
    cta: { fr: "Demander l'accès API", en: "Request API access" },
    ctaTo: "#contact",
    tier: "pro",
  },
];

const TIER_LABEL = {
  amateur: { fr: "Amateur+", en: "Amateur+", color: "bg-[#FCE4ED] text-[#E5276A]" },
  pro: { fr: "Pro", en: "Pro", color: "bg-[#1F3A5F] text-white" },
};

function FeatureCard({ f, lang, expanded, onToggle, chatHistories, setChatHistory }) {
  const Icon = f.icon;
  const tier = TIER_LABEL[f.tier];
  const [chatOpen, setChatOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const history = chatHistories[f.key] || [];

  const SUGGESTIONS = lang === "fr"
    ? ["Combien ça me rapporte vraiment ?", "Et si ça ne me convient pas ?", "Compare avec la version gratuite", "Combien de temps pour démarrer ?"]
    : ["What's the real ROI?", "What if it doesn't fit?", "Compare with the free version", "How long to start?"];

  const ask = async (text) => {
    const question = (text || q).trim();
    if (!question) return;
    setBusy(true);
    setChatHistory(f.key, [...history, { role: "user", text: question }]);
    setQ("");
    try {
      const { data } = await api.post("/pro/sales-chat", { feature_key: f.key, question, lang });
      setChatHistory(f.key, (prev) => [...prev, {
        role: "ai",
        text: data.answer,
        arguments: data.arguments || [],
        action: data.best_action,
        action_label: data.action_label,
        honest_note: data.honest_note,
      }]);
    } catch (e) {
      setChatHistory(f.key, (prev) => [...prev, {
        role: "ai",
        text: e?.response?.data?.detail || (lang === "fr" ? "geoVINUM AI est indisponible." : "geoVINUM AI unavailable."),
      }]);
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`app-card transition-all overflow-hidden ${expanded ? "border-[#E5276A] shadow-lg" : ""}`}
      data-testid={`pro-feature-${f.key}`}
    >
      <button
        onClick={onToggle}
        className="w-full p-5 md:p-6 flex items-start gap-4 text-left hover:bg-[#FBF7F3] transition-colors"
        data-testid={`pro-feature-toggle-${f.key}`}
      >
        <div className="w-12 h-12 rounded-xl bg-[#FCE4ED] flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6 text-[#E5276A]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl text-[#1F3A5F] leading-tight">{f.title[lang] || f.title.fr}</h3>
            <span className={`text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-full ${tier.color}`}>{tier[lang] || tier.fr}</span>
          </div>
          <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{f.pitch[lang] || f.pitch.fr}</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-[#8B95A1] shrink-0 transition-transform ${expanded ? "rotate-180 text-[#E5276A]" : ""}`} />
      </button>
      {expanded && (
        <div className="px-5 md:px-6 pb-6 pt-2 border-t border-[#1F3A5F]/8 ml-16" data-testid={`pro-feature-details-${f.key}`}>
          <ul className="space-y-2 mt-3">
            {(f.details[lang] || f.details.fr).map((d, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#1F3A5F]">
                <div className="w-4 h-4 rounded-full bg-[#E5276A] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </div>
                <span className="font-light">{d}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            {f.ctaTo && (
              <Link
                to={f.ctaTo}
                data-testid={`pro-feature-cta-${f.key}`}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-[#E5276A] hover:underline"
              >
                {f.cta[lang] || f.cta.fr} <ChevronRight className="w-4 h-4" />
              </Link>
            )}
            <button
              onClick={() => setChatOpen((v) => !v)}
              data-testid={`pro-feature-ask-${f.key}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]"
            >
              <MessageCircle className="w-3.5 h-3.5" /> {lang === "fr" ? "Poser une question à geoVINUM AI" : "Ask geoVINUM AI"}
            </button>
          </div>

          {chatOpen && (
            <div className="mt-4 p-4 rounded-2xl bg-[#FBF7F3] border border-[#E5276A]/20" data-testid={`pro-feature-chat-${f.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#E5276A] flex items-center justify-center text-white text-[10px] font-bold">A</div>
                  <span className="text-xs font-bold text-[#1F3A5F]">{lang === "fr" ? "geoVINUM AI · conseillère" : "geoVINUM AI · advisor"}</span>
                </div>
                <button onClick={() => setChatOpen(false)} className="text-[#8B95A1] hover:text-[#1F3A5F]"><X className="w-4 h-4" /></button>
              </div>
              <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                {history.length === 0 && (
                  <p className="text-xs text-[#4A5568] italic">
                    {lang === "fr" ? "Posez n'importe quelle question — réponses honnêtes, sans pression." : "Ask anything — honest answers, no pressure."}
                  </p>
                )}
                {history.map((msg, i) => (
                  <div key={i} className={msg.role === "user" ? "text-right" : "text-left"} data-testid={`pro-feature-msg-${f.key}-${i}`}>
                    <div className={`inline-block max-w-[85%] p-3 rounded-xl text-sm ${
                      msg.role === "user" ? "bg-[#1F3A5F] text-white" : "bg-white text-[#1F3A5F] border border-[#1F3A5F]/8"
                    }`}>
                      {msg.text}
                      {msg.honest_note && (
                        <div className="mt-2 text-xs italic text-amber-700 bg-amber-50 p-2 rounded-lg">⚠️ {msg.honest_note}</div>
                      )}
                      {msg.arguments?.length > 0 && (
                        <ul className="mt-2 space-y-1">{msg.arguments.map((a, j) => (
                          <li key={j} className="text-xs flex gap-1.5"><span className="text-[#E5276A]">→</span> {a}</li>
                        ))}</ul>
                      )}
                      {msg.action_label && msg.action !== "try_free" && (
                        <Link to={msg.action === "request_demo" ? "/pro#contact" : "/pricing"} data-testid={`pro-feature-msg-action-${f.key}-${i}`}
                          className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E5276A] text-white text-xs font-bold hover:bg-[#C01661]">
                          {msg.action_label} <ChevronRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="text-left"><div className="inline-block p-3 rounded-xl bg-white border border-[#1F3A5F]/8 text-xs text-[#8B95A1]">
                    <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> {lang === "fr" ? "geoVINUM AI réfléchit…" : "geoVINUM AI is thinking…"}
                  </div></div>
                )}
              </div>
              {history.length === 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">{SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => ask(s)} data-testid={`pro-feature-suggest-${f.key}-${i}`}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-[#1F3A5F]/12 text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]">
                    {s}
                  </button>
                ))}</div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "fr" ? "Votre question…" : "Your question…"}
                  data-testid={`pro-feature-input-${f.key}`}
                  className="flex-1 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-3 py-2 text-sm text-[#1F3A5F]" />
                <button type="submit" disabled={busy || !q} data-testid={`pro-feature-send-${f.key}`}
                  className="px-3 py-2 rounded-lg bg-[#E5276A] text-white disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Pro() {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const presetApp = params.get("app") || "";
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", message: "" });
  const [sent, setSent] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all");
  const [chatHistories, setChatHistories] = useState({});

  // Pro subscribers (and admins) see a shortcut hub instead of the sales pitch.
  const isProUser = !!(user && (user.plan === "pro" || user.role === "admin"));

  const setChatHistory = (featureKey, updater) => {
    setChatHistories((prev) => {
      const next = typeof updater === "function" ? updater(prev[featureKey] || []) : updater;
      return { ...prev, [featureKey]: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, appellation_context: presetApp || null });
      setSent(true);
      toast.success(t("pro.toast.sent"));
    } catch {
      toast.error(t("pro.toast.error"));
    }
  };

  const filtered = filter === "all" ? FEATURES : FEATURES.filter((f) => f.tier === filter);

  // ─── Pro user shortcut hub ───
  if (isProUser) {
    const HUB_TILES = [
      { key: "dashboard", to: "/pro/dashboard", icon: LayoutGrid,
        title: { fr: "Tableau de bord", en: "Dashboard" },
        body:  { fr: "Vue d'ensemble, crédits, leads, stats.", en: "Overview, credits, leads, stats." } },
      { key: "story",     to: "/producers/mine", icon: Building2,
        title: { fr: "Fiche domaine",  en: "Winery page" },
        body:  { fr: "Onboarding IA + Story Builder 7 sections.", en: "AI onboarding + 7-section Story Builder." } },
      { key: "map",       to: "/pro/map-builder", icon: MapIcon,
        title: { fr: "Map Builder",    en: "Map Builder" },
        body:  { fr: "Parcours de visite, parcellaire, affiche 360°.", en: "Visit routes, parcels, 360° posters." } },
      { key: "sponsored", to: "/pro/sponsored", icon: Megaphone,
        title: { fr: "Quiz sponsorisés", en: "Sponsored quizzes" },
        body:  { fr: "Quiz IA + QR + capture de leads qualifiés.", en: "AI quizzes + QR + qualified lead capture." } },
      { key: "experience", to: "/pro/experience", icon: CalendarClock,
        title: { fr: "Événements Experience", en: "Experience events" },
        body:  { fr: "Billetterie Stripe, QR staff, gamification.", en: "Stripe ticketing, staff QR, gamification." },
        badge: "NEW" },
    ];
    return (
      <div data-testid="pro-page" className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
        <Toaster theme="light" />
        <div>
          <span className="chip-pink">{lang === "fr" ? "Espace Pro" : "Pro space"}</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
            {lang === "fr" ? "Bienvenue dans votre " : "Welcome to your "}
            <span className="text-[#E5276A]">{lang === "fr" ? "Espace Pro" : "Pro space"}</span>
          </h1>
          <p className="text-[#4A5568] mt-3 max-w-2xl">
            {lang === "fr"
              ? "Vos 5 builders IA à portée de main. Créez, publiez, partagez — geoVINUM AI fait le gros du travail."
              : "Your 5 AI builders in one place. Create, publish, share — geoVINUM AI does the heavy lifting."}
          </p>
        </div>

        <div className="mt-8">
          <ProOnboardingChecklist />
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-2" data-testid="pro-hub-tiles">
          {HUB_TILES.map((tile) => {
            const Icon = tile.icon;
            return (
              <Link
                key={tile.key}
                to={tile.to}
                data-testid={`pro-hub-tile-${tile.key}`}
                className="group rounded-2xl border border-[#1F3A5F]/12 bg-white hover:border-[#E5276A]/40 hover:shadow-md transition-all p-6"
              >
                <div className="flex items-start justify-between">
                  <span className="w-11 h-11 rounded-xl bg-[#FCE4ED] text-[#E5276A] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="flex items-center gap-2">
                    {tile.badge && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E5276A] text-white font-bold tracking-wider">
                        {tile.badge}
                      </span>
                    )}
                    <ChevronRight className="w-5 h-5 text-[#8B95A1] group-hover:text-[#E5276A] group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
                <h3 className="font-display text-xl text-[#1F3A5F] mt-4 leading-tight">
                  {tile.title[lang === "fr" ? "fr" : "en"]}
                </h3>
                <p className="text-sm text-[#4A5568] mt-1.5 leading-relaxed">
                  {tile.body[lang === "fr" ? "fr" : "en"]}
                </p>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/guide"
            data-testid="pro-hub-guide"
            className="inline-flex items-center gap-2 text-sm text-[#8B95A1] hover:text-[#E5276A] font-bold"
          >
            {lang === "fr" ? "Consulter le mode d'emploi Pro" : "Read the Pro user guide"}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pro-page">
      <Toaster theme="light" />
      <section className="max-w-5xl mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-10 text-center">
        <span className="chip-pink mx-auto">{t("pro.kicker")}</span>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight text-[#1F3A5F] mt-6 font-medium leading-[1.05]">{t("pro.heroTitle")}</h1>
        <p className="mt-6 text-lg text-[#4A5568] max-w-2xl mx-auto font-light leading-relaxed">{t("pro.heroBody")}</p>
        {presetApp && (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E5276A]/30 bg-[#FCE4ED]/50 text-sm text-[#E5276A]">
            {t("pro.preset")} <strong className="text-[#1F3A5F]">{presetApp}</strong>
          </div>
        )}
      </section>

      {/* ━━━━━ INTERACTIVE FEATURE CATALOG ━━━━━ */}
      <section className="max-w-4xl mx-auto px-5 md:px-10 py-8" data-testid="pro-catalog">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <span className="chip-pink inline-flex items-center gap-1.5"><Wand2 className="w-3 h-3" /> {lang === "fr" ? "Catalogue Pro" : "Pro catalog"}</span>
            <h2 className="font-display text-3xl md:text-4xl text-[#1F3A5F] font-medium mt-3 leading-tight">
              {lang === "fr" ? "Tout ce qui débloque l'Espace Pro" : "Everything the Pro space unlocks"}
            </h2>
          </div>
          <div className="inline-flex gap-1 p-1 rounded-full bg-[#FBF7F3]" data-testid="pro-catalog-filter">
            {["all", "amateur", "pro"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`pro-filter-${k}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  filter === k ? "bg-[#E5276A] text-white" : "text-[#1F3A5F] hover:bg-white"
                }`}
              >
                {k === "all" ? (lang === "fr" ? "Toutes" : "All") : k === "amateur" ? "Amateur+" : "Pro"}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-[#4A5568] mb-6 max-w-2xl">
          {lang === "fr"
            ? "Cliquez sur chaque fonctionnalité pour la voir en détail. Toutes sont incluses dans l'abonnement Pro (49 €/mois). Le plan Amateur (29 €/mois) débloque les fonctionnalités marquées Amateur+."
            : "Click each feature to see details. All are included in the Pro plan (€49/mo). The Amateur plan (€29/mo) unlocks features marked Amateur+."}
        </p>
        <div className="space-y-3">
          {filtered.map((f) => (
            <FeatureCard
              key={f.key}
              f={f}
              lang={lang}
              expanded={!!expanded[f.key]}
              onToggle={() => setExpanded({ ...expanded, [f.key]: !expanded[f.key] })}
              chatHistories={chatHistories}
              setChatHistory={setChatHistory}
            />
          ))}
        </div>
        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <Link to="/pricing" data-testid="pro-catalog-pricing"
            className="app-card p-5 hover:border-[#E5276A] block">
            <Sparkles className="w-5 h-5 text-[#E5276A]" />
            <h3 className="font-bold text-[#1F3A5F] mt-3">{lang === "fr" ? "Voir tous les plans" : "See all plans"}</h3>
            <p className="text-xs text-[#4A5568] mt-1">{lang === "fr" ? "Comparatif détaillé Découverte / Amateur / Pro" : "Detailed Discovery / Amateur / Pro comparison"}</p>
          </Link>
          <Link to="/guide" data-testid="pro-catalog-guide"
            className="app-card p-5 hover:border-[#E5276A] block">
            <BookOpenIcon />
            <h3 className="font-bold text-[#1F3A5F] mt-3">{lang === "fr" ? "Mode d'emploi" : "User guide"}</h3>
            <p className="text-xs text-[#4A5568] mt-1">{lang === "fr" ? "Cas d'usage et tutoriels pour démarrer" : "Use cases and tutorials to get started"}</p>
          </Link>
          <a href="#contact" data-testid="pro-catalog-contact"
            className="app-card p-5 hover:border-[#E5276A] block">
            <ShieldCheck className="w-5 h-5 text-[#E5276A]" />
            <h3 className="font-bold text-[#1F3A5F] mt-3">{lang === "fr" ? "Parler à un humain" : "Talk to a human"}</h3>
            <p className="text-xs text-[#4A5568] mt-1">{lang === "fr" ? "Démo personnalisée pour votre domaine" : "Personalised demo for your domain"}</p>
          </a>
        </div>
      </section>

      <section className="bg-[#FBF7F3] mt-12 py-20">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <span className="chip-pink">{t("pro.who.kicker")}</span>
          <h2 className="font-display text-3xl md:text-4xl text-[#1F3A5F] font-medium mt-4 mb-8">{t("pro.who.title")}</h2>
          <ul className="grid sm:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="flex items-start gap-3 text-[#1F3A5F]">
                <div className="w-5 h-5 rounded-full bg-[#E5276A] flex items-center justify-center mt-0.5 shrink-0">
                  <Check className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
                <span className="text-base font-light">{t(`pro.who.${i}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="contact" className="max-w-3xl mx-auto px-5 md:px-10 py-20">
        <span className="chip-pink">{t("pro.form.kicker")}</span>
        <h2 className="font-display text-3xl md:text-4xl text-[#1F3A5F] font-medium mt-4 mb-8">{t("pro.form.title")}</h2>

        {sent ? (
          <div data-testid="pro-success" className="p-10 rounded-3xl border border-[#E5276A]/20 bg-[#FCE4ED]/40 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#E5276A] flex items-center justify-center mb-4">
              <Check className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
            <div className="font-display text-2xl text-[#1F3A5F] font-medium">{t("pro.success.title")}</div>
            <p className="text-[#4A5568] mt-2">{t("pro.success.body")}</p>
          </div>
        ) : (
          <form data-testid={PRO.form} onSubmit={submit} className="grid md:grid-cols-2 gap-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
            <Field label={t("pro.form.name")}>
              <input data-testid={PRO.name} required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label={t("pro.form.email")}>
              <input data-testid={PRO.email} required type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label={t("pro.form.company")}>
              <input data-testid={PRO.company} className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </Field>
            <Field label={t("pro.form.website")}>
              <input data-testid={PRO.website} className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" />
            </Field>
            <Field label={t("pro.form.message")} full>
              <textarea data-testid={PRO.message} rows={4} className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("pro.form.placeholder")} />
            </Field>
            <div className="md:col-span-2">
              <button data-testid={PRO.submit} className="w-full px-6 py-4 rounded-xl bg-[#E5276A] hover:bg-[#C01661] text-white text-base font-medium transition-colors">
                {t("pro.form.submit")}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function BookOpenIcon() {
  // Tiny inline icon to avoid extra import
  return <svg className="w-5 h-5 text-[#E5276A]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>;
}

const inputCls = "w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]";
const Field = ({ label, children, full }) => (
  <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">{label}</span>
    {children}
  </label>
);
